<!-- ============================================================ -->
<!-- MODAL FULLSCREEN: el wizard se renderiza como overlay a pantalla completa. -->
<!-- z-modal del sistema de tokens (nunca valores crudos). El backdrop es hermano del panel. -->
<!-- ============================================================ -->
<div class="fixed inset-0 z-modal flex flex-col">
  <!-- Backdrop -->
  <div class="absolute inset-0 bg-gray-900/60 dark:bg-black/70" (click)="cerrarModal()"></div>

  <!-- Panel del modal (header fijo + cuerpo con scroll interno + footer fijo) -->
  <div class="relative flex flex-col w-full h-full bg-gray-50 dark:bg-gray-950 overflow-hidden">

    <!-- ===================== HEADER FIJO ===================== -->
    <div class="shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-3 min-w-0">
        <h1 class="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
          {{ isEditing ? 'Editar cotización' : 'Nueva cotización' }}
        </h1>
        @if (isEditing && currentQuote?.quoteNumber) {
        <span class="hidden sm:inline text-sm font-medium text-gray-500 dark:text-gray-400">{{ currentQuote?.quoteNumber }}</span>
        }
        @if (isEditing && currentQuote?.version) {
        <span class="hidden sm:inline px-2 py-0.5 text-xs font-semibold rounded-full bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400"
          title="Versión vigente">v{{ currentQuote?.version }}</span>
        }
      </div>
      <button type="button" (click)="cerrarModal()" aria-label="Cerrar"
        class="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <!-- Loading (ocupa todo el cuerpo del modal) -->
    @if (loading) {
    <div class="flex-1 flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
      <span class="ml-3 text-gray-500 dark:text-gray-400">Cargando cotización...</span>
    </div>
    }

@if (!loading) {
<form [formGroup]="form" (ngSubmit)="guardar()" class="flex-1 flex flex-col min-h-0">

  <!-- ===================== CUERPO CON SCROLL INTERNO ===================== -->
  <div class="flex-1 overflow-y-auto">
    <div class="p-4 sm:p-6">

<div class="space-y-3 mb-4">
  <!-- Error -->
  @if (error) {
  <div class="p-4 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20">
    <div class="flex items-center gap-3">
      <svg class="w-5 h-5 text-error-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <p class="text-error-600 dark:text-error-400">{{ error }}</p>
    </div>
  </div>
  }

  <!-- Success -->
  @if (successMessage) {
  <div class="p-4 bg-success-50 border border-success-200 rounded-lg dark:bg-success-500/10 dark:border-success-500/20">
    <div class="flex items-center gap-3">
      <svg class="w-5 h-5 text-success-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>
      <p class="text-success-600 dark:text-success-400">{{ successMessage }}</p>
    </div>
  </div>
  }

  <!-- Quote status badge (edit mode) -->
  @if (isEditing && currentQuote) {
  <div class="flex items-center gap-3">
    <span class="text-sm text-gray-500 dark:text-gray-400">Estado actual:</span>
    <span class="px-2.5 py-1 text-xs font-semibold rounded-full"
      [class]="currentQuote.statusName === 'Aceptada' ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400' :
               currentQuote.statusName === 'Publicada' ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400' :
               currentQuote.statusName === 'Rechazada' ? 'bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400' :
               currentQuote.statusName === 'Expirada' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
               'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'">
      {{ currentQuote.statusName }}
    </span>
    @if (currentQuote.quoteNumber) {
    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ currentQuote.quoteNumber }}</span>
    }
    @if (currentQuote.version) {
    <span class="px-2 py-0.5 text-xs font-semibold rounded-full bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400"
      title="Versión vigente de esta cotización">v{{ currentQuote.version }}</span>
    }
    <button type="button" (click)="verVersiones()"
      class="flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors">
      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>
      Historial de versiones
    </button>
  </div>
  }
</div>

  <!-- ====================== STEPPER (barra de progreso) ====================== -->
  <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-5 mb-4">
    <ol class="flex items-center w-full">
      @for (s of steps; track s.num; let last = $last) {
      <li class="flex items-center" [class.flex-1]="!last">
        <button type="button" (click)="goToStep(s.num)"
          [disabled]="!isEditing && s.num > currentStep + 1"
          [title]="(!isEditing && s.num > currentStep + 1) ? 'Completa el paso actual para avanzar' : 'Ir a: ' + s.title"
          class="group flex items-center gap-2.5 text-left rounded-lg px-1.5 py-1 -mx-1.5 transition-colors enabled:hover:bg-gray-50 dark:enabled:hover:bg-gray-800/60 enabled:cursor-pointer disabled:cursor-not-allowed"
          [attr.aria-current]="currentStep === s.num ? 'step' : null">
          <!-- Círculo del paso -->
          <span class="shrink-0 flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-colors"
            [ngClass]="currentStep === s.num
              ? 'bg-brand-500 text-white ring-4 ring-brand-100 dark:ring-brand-900/40'
              : (currentStep > s.num
                  ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-400'
                  : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500')">
            @if (currentStep > s.num && isStepValid(s.num)) {
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
            </svg>
            } @else {
            {{ s.num }}
            }
          </span>
          <!-- Texto del paso (oculto en móvil salvo el actual) -->
          <span class="hidden sm:block min-w-0" [class.sm:hidden]="false">
            <span class="block text-sm font-medium leading-tight"
              [ngClass]="currentStep === s.num ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'">{{ s.title }}</span>
            <span class="block text-xs text-gray-400 dark:text-gray-500 leading-tight">{{ s.subtitle }}</span>
          </span>
        </button>
        <!-- Conector -->
        @if (!last) {
        <span class="flex-1 mx-2 sm:mx-3 h-0.5 rounded transition-colors"
          [ngClass]="currentStep > s.num ? 'bg-brand-400 dark:bg-brand-600' : 'bg-gray-200 dark:bg-gray-700'"></span>
        }
      </li>
      }
    </ol>
    <!-- Título del paso actual (visible en móvil) -->
    <p class="sm:hidden mt-3 text-sm font-medium text-gray-900 dark:text-white">
      Paso {{ currentStep }} de {{ totalSteps }}: {{ steps[currentStep - 1].title }}
    </p>
  </div>

  <!-- ====================== LAYOUT ADAPTATIVO: contenido + sidebar sticky ====================== -->
  <!-- Esquema responsivo de columnas: -->
  <!--   < lg (1024)        → 1 columna apilada (sidebar arriba, ver order-first) -->
  <!--   lg–xl (1024–1279)  → 2 columnas = contenido (1 col interna) + sidebar -->
  <!--   ≥ xl (1280)        → 3 columnas = contenido (2 cols internas balanceadas) + sidebar -->
  <!-- El ancho máximo se amplía desde xl para que las 3 columnas tengan ancho real (1920 cae cómodo). -->
  <div class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4 lg:gap-6 items-start max-w-4xl lg:max-w-5xl xl:max-w-[1500px] 2xl:max-w-[1760px] 3xl:max-w-[1960px] mx-auto">

  <!-- ====================== CONTENIDO DEL PASO (columna izquierda) ====================== -->
  <div class="space-y-4 min-w-0">

  <!-- ============ PASO 1 — DATOS GENERALES ============ -->
  @if (currentStep === 1) {
    <!-- ≥ xl: distribución real y balanceada en 2 columnas internas (columna izq. = formulario principal, -->
    <!-- columna der. = datos del cliente + notas). items-start evita que una columna estire a la otra. -->
    <div class="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">

      <!-- Columna izquierda -->
      <div class="space-y-4 min-w-0">

      <!-- Card: Quote Info -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
        <div>
          <h2 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Datos generales</h2>
          <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Los campos con <span class="text-error-500">*</span> son obligatorios.</p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Deal -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Negocio <span class="text-error-500">*</span>
            </label>
            <app-entity-picker
              placeholder="Buscar negocio... (Enter para ver todos)"
              [selectedId]="dealPickerSelected?.id ?? null"
              [selectedLabel]="dealPickerSelected?.label ?? null"
              [searchFn]="searchDeals"
              (selectionChange)="onDealPicked($event)" />
            @if (hasError('dealId')) {
            <p class="mt-1 text-xs text-error-600">{{ getError('dealId') }}</p>
            }
          </div>

          <!-- Cliente / contacto principal (editable). Se precarga con el contacto primario del negocio. -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Cliente / contacto</label>
            <app-entity-select
              [value]="form.value.idContactPrimary"
              (selected)="onContactPicked($event)"
              [displayText]="contactPickerDisplay"
              [searchFn]="searchContacts"
              [labelFn]="contactLabel"
              [sublabelFn]="contactSublabel"
              placeholder="Buscar contacto por nombre o correo…" />
            <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Se autocompleta con el contacto principal del negocio; puedes cambiarlo.</p>
          </div>

          <!-- Status -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Estado</label>
            @if (isEditing) {
            <select formControlName="quoteStatusId"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
              <option [ngValue]="null">Seleccione un estado</option>
              @for (status of quoteStatuses; track status.id) {
              <option [ngValue]="status.id">{{ status.name }}</option>
              }
            </select>
            } @else {
            <!--
              (INC28) En creación el estado NO es elegible: toda cotización nueva nace en Borrador y el
              backend fuerza ese estado ignorando el quoteStatusId del request. Se muestra de solo lectura.
              No se usa [disabled] ni control.disable(): un control deshabilitado no entra en form.value
              y rompería el armado del request — el control queda seteado y solo se oculta el <select>.
            -->
            <div class="flex h-[38px] w-full items-center rounded-lg border border-gray-200 bg-gray-50 px-3 dark:border-gray-700 dark:bg-gray-800/50">
              <app-quote-status-badge [status]="draftStatusName" />
            </div>
            <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Toda cotización nueva se crea como borrador. Podrás cambiar su estado después de guardarla.</p>
            }
          </div>

          <!-- Date Issued -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Fecha de Emisión</label>
            <input
              type="date"
              id="datetimeIssued"
              formControlName="datetimeIssued"
              (click)="$any($event.target).showPicker?.()"
              (focus)="$any($event.target).showPicker?.()"
              class="dark:bg-dark-900 dark:[color-scheme:dark] h-11 w-full cursor-pointer rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
              [class.border-error-500]="hasError('datetimeIssued')"
              [class.border-gray-300]="!hasError('datetimeIssued')"
            />
            @if (hasError('datetimeIssued')) {
            <p class="mt-1 text-xs text-error-600 dark:text-error-400">{{ getError('datetimeIssued') }}</p>
            }
          </div>

          <!-- Date Expires -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Válido Hasta</label>
            <input
              type="date"
              id="datetimeExpires"
              formControlName="datetimeExpires"
              (click)="$any($event.target).showPicker?.()"
              (focus)="$any($event.target).showPicker?.()"
              class="dark:bg-dark-900 dark:[color-scheme:dark] h-11 w-full cursor-pointer rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
              [class.border-error-500]="dateRangeError || hasError('datetimeExpires')"
              [class.border-gray-300]="!dateRangeError && !hasError('datetimeExpires')"
            />
            @if (hasError('datetimeExpires')) {
            <p class="mt-1 text-xs text-error-600 dark:text-error-400">{{ getError('datetimeExpires') }}</p>
            } @else if (dateRangeError) {
            <p class="mt-1 text-xs text-error-600 dark:text-error-400">Debe ser posterior a la fecha de emisión</p>
            }
          </div>

          <!-- Moneda (movida del sidebar al paso 1) -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Moneda</label>
            <select formControlName="currencyId" (change)="onCurrencyManuallyChanged()"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
              <option [ngValue]="null">— Sin moneda —</option>
              @for (c of currencies; track c.id) {
              <option [ngValue]="c.id">{{ c.prefix || c.code }} · {{ c.name }}</option>
              }
            </select>
          </div>

          <!-- Lista de precios (V75) -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Lista de precios</label>
            <select formControlName="priceListId"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
              <option [ngValue]="null">— Sin lista de precios —</option>
              @for (pl of priceLists; track pl.id) {
              <option [ngValue]="pl.id">{{ pl.name }}@if (pl.isDefault) { (predeterminada) }</option>
              }
            </select>
            <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Al agregar productos, se prefijará su precio según esta lista.</p>
          </div>

          <!-- Supervisor (V75) -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Supervisor</label>
            <select formControlName="supervisorUserId"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
              <option [ngValue]="null">— Sin supervisor —</option>
              @for (u of supervisors; track u.id) {
              <option [ngValue]="u.id">{{ supervisorLabel(u) }}</option>
              }
            </select>
          </div>

          <!-- Probabilidad de cierre (READ-ONLY del deal, V75) -->
          @if (dealProbability !== null) {
          <div class="flex items-end">
            <div class="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-700/40">
              <svg class="w-4 h-4 text-brand-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
              </svg>
              <span class="text-sm text-gray-700 dark:text-gray-300">Probabilidad de cierre: <strong class="text-brand-600 dark:text-brand-400">{{ dealProbability }}%</strong></span>
            </div>
          </div>
          }
        </div>
      </div>

      </div><!-- /columna izquierda paso 1 -->

      <!-- Columna derecha -->
      <div class="space-y-4 min-w-0">

      <!-- Datos heredados del negocio (deal → empresa → contacto primario → vendedor), solo lectura -->
      @if (isEditing && currentQuote) {
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-3">
        <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Datos del Cliente</h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">

        <!-- Empresa -->
        @if (currentQuote.companyCommercialName) {
        <div class="flex items-start gap-2">
          <svg class="w-4 h-4 mt-0.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
          </svg>
          <div class="min-w-0">
            <p class="text-xs text-gray-500 dark:text-gray-400">Empresa</p>
            <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ currentQuote.companyCommercialName }}</p>
          </div>
        </div>
        }

        <!-- Contacto primario -->
        @if (currentQuote.contactFullName || currentQuote.contactEmail || currentQuote.contactPhone) {
        <div class="flex items-start gap-2">
          <svg class="w-4 h-4 mt-0.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
          </svg>
          <div class="min-w-0">
            <p class="text-xs text-gray-500 dark:text-gray-400">Contacto</p>
            @if (currentQuote.contactFullName) {
            <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ currentQuote.contactFullName }}</p>
            }
            @if (currentQuote.contactEmail) {
            <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ currentQuote.contactEmail }}</p>
            }
            @if (currentQuote.contactPhone) {
            <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ currentQuote.contactPhone }}</p>
            }
          </div>
        </div>
        }

        <!-- Vendedor -->
        @if (currentQuote.sellerFullName || currentQuote.sellerEmail) {
        <div class="flex items-start gap-2">
          <svg class="w-4 h-4 mt-0.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
          </svg>
          <div class="min-w-0">
            <p class="text-xs text-gray-500 dark:text-gray-400">Vendedor</p>
            @if (currentQuote.sellerFullName) {
            <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ currentQuote.sellerFullName }}</p>
            }
            @if (currentQuote.sellerEmail) {
            <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ currentQuote.sellerEmail }}</p>
            }
          </div>
        </div>
        }

        <!-- Moneda -->
        @if (currentQuote.currencyCode || currentQuote.currencySymbol) {
        <div class="flex items-start gap-2">
          <svg class="w-4 h-4 mt-0.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <div class="min-w-0">
            <p class="text-xs text-gray-500 dark:text-gray-400">Moneda</p>
            <p class="text-sm font-medium text-gray-900 dark:text-white">
              {{ currentQuote.currencyCode || '' }}
              @if (currentQuote.currencySymbol) {
              <span class="text-gray-500 dark:text-gray-400">({{ currentQuote.currencySymbol }})</span>
              }
            </p>
          </div>
        </div>
        }
        </div>

        <!-- Branding de la plantilla aplicada -->
        @if (currentQuote.templateLogoUrl || currentQuote.templatePrimaryColor || currentQuote.templateHeaderText) {
        <div class="border-t border-gray-100 dark:border-gray-800 pt-3">
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">Branding aplicado al PDF</p>
          <div class="flex items-center gap-3">
            @if (currentQuote.templateLogoUrl) {
            <img [src]="currentQuote.templateLogoUrl" alt="Logo de la plantilla"
              class="h-9 w-9 rounded border border-gray-200 dark:border-gray-700 object-contain bg-white"/>
            }
            @if (currentQuote.templatePrimaryColor) {
            <span class="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
              <span class="w-4 h-4 rounded-full border border-gray-200 dark:border-gray-600 inline-block"
                [style.background-color]="currentQuote.templatePrimaryColor"></span>
              {{ currentQuote.templatePrimaryColor }}
            </span>
            }
          </div>
          @if (currentQuote.templateHeaderText) {
          <p class="mt-2 text-xs italic text-gray-500 dark:text-gray-400 truncate" [title]="currentQuote.templateHeaderText">
            “{{ currentQuote.templateHeaderText }}”
          </p>
          }
        </div>
        }
      </div>
      }

      <!-- (Incidente 13) Información del Cliente al crear desde un negocio: contacto principal + empresa.
           En edición ya se muestra la tarjeta "Datos del Cliente" de arriba. -->
      @if (!isEditing && dealClientInfo) {
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-3">
        <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Información del Cliente</h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <!-- Contacto principal -->
          <div class="flex items-start gap-2">
            <svg class="w-4 h-4 mt-0.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
            </svg>
            <div class="min-w-0">
              <p class="text-xs text-gray-500 dark:text-gray-400">Contacto</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ dealClientInfo.name }}</p>
              @if (dealClientInfo.email) {
              <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ dealClientInfo.email }}</p>
              }
              @if (dealClientInfo.phone) {
              <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ dealClientInfo.phone }}</p>
              }
            </div>
          </div>
          <!-- Empresa -->
          @if (dealClientCompany) {
          <div class="flex items-start gap-2">
            <svg class="w-4 h-4 mt-0.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
            </svg>
            <div class="min-w-0">
              <p class="text-xs text-gray-500 dark:text-gray-400">Empresa</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ dealClientCompany }}</p>
            </div>
          </div>
          }
        </div>
      </div>
      }

      <!-- Notas y mensajes (movidas desde el antiguo paso 4) -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
        <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Notas y mensajes</h3>

        <!-- Notas (cliente) -->
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Notas</label>
          <textarea formControlName="notes" rows="2" maxlength="2000"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            placeholder="Notas adicionales sobre la cotización..."></textarea>
        </div>

        <!-- Mensaje de apertura (visible al cliente) -->
        <div class="border-t border-gray-100 dark:border-gray-800 pt-3">
          <label class="flex items-center gap-1.5 text-xs font-medium text-brand-600 dark:text-brand-400 mb-1">
            <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
            </svg>
            Mensaje de apertura
          </label>
          <p class="text-[11px] text-brand-600/80 dark:text-brand-400/80 mb-1">El cliente lo verá al inicio de la cotización (portal y PDF) y en el correo de envío.</p>
          <textarea formControlName="openingMessage" rows="2" maxlength="2000"
            class="w-full px-3 py-2 text-sm border border-brand-300 dark:border-brand-700/60 rounded-lg bg-brand-50/40 dark:bg-brand-900/10 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            placeholder="Gracias por tu interés. Preparamos esta propuesta según lo conversado..."></textarea>
          <div class="mt-1 text-right text-[11px] text-gray-400 dark:text-gray-500">{{ (form.get('openingMessage')?.value || '').length }}/2000</div>
        </div>

        <!-- Notas internas (solo equipo) -->
        <div class="border-t border-gray-100 dark:border-gray-800 pt-3">
          <label class="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">
            <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"/>
            </svg>
            Notas internas
          </label>
          <p class="text-[11px] text-amber-600 dark:text-amber-500 mb-1">Solo visibles para tu equipo — el cliente nunca las verá (no aparecen en el portal ni en el PDF).</p>
          <textarea formControlName="internalNotes" rows="2" maxlength="5000"
            class="w-full px-3 py-2 text-sm border border-amber-300 dark:border-amber-700/60 rounded-lg bg-amber-50/50 dark:bg-amber-900/10 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="Contexto comercial, acuerdos internos, límites de descuento..."></textarea>
          <div class="mt-1 text-right text-[11px] text-gray-400 dark:text-gray-500">{{ (form.get('internalNotes')?.value || '').length }}/5000</div>
        </div>

        <!-- Notas de versión -->
        <div class="border-t border-gray-100 dark:border-gray-800 pt-3">
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Notas de Versión</label>
          <input type="text" formControlName="versionNotes" maxlength="500"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            placeholder="Descripción de cambios en esta versión (opcional)">
        </div>
      </div>

      </div><!-- /columna derecha paso 1 -->

    </div>
  }

  <!-- ============ PASO 2 — ÍTEMS ============ -->
  @if (currentStep === 2) {
    <div class="space-y-4">
      <!-- Card: Items -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Ítems</h2>
          @if (!showAddItemForm) {
          <div class="flex items-center gap-2">
            <button type="button" (click)="openImportItemsModal()"
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"/>
              </svg>
              Importar ítems
            </button>
            <!-- (INC34) Alta de PAQUETE: acción separada del alta de ítem. El paquete no es un tipo
                 de ítem: se expande en una línea de producto por componente. -->
            <button type="button" (click)="abrirAgregarPaquete()"
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-purple-300 dark:border-purple-700/60 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
              </svg>
              Añadir paquete
            </button>
            <button type="button" (click)="openAddItemForm()"
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 transition-colors">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
              Agregar Ítem
            </button>
          </div>
          }
        </div>

        <!-- Buscador + Filtros (filtrado en cliente sobre los ítems ya agregados) -->
        @if (items.length > 0) {
        <div class="mb-3 flex flex-col sm:flex-row sm:items-center gap-2">
          <!-- Buscador -->
          <div class="relative flex-1 min-w-0">
            <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/>
            </svg>
            <input type="text" [(ngModel)]="itemSearchTerm" [ngModelOptions]="{standalone: true}"
              placeholder="Buscar ítem por nombre, código o descripción..."
              class="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"/>
          </div>
          <!-- Filtro por tipo -->
          <select [(ngModel)]="itemTypeFilter" [ngModelOptions]="{standalone: true}"
            class="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
            <option value="">Todos los tipos</option>
            <option value="product">Productos</option>
            <option value="service_resource">Servicios · Recursos</option>
            <option value="service_consulting">Servicios · Consultoría</option>
            <option value="custom">Personalizado</option>
          </select>
          <!-- Filtro por categoría -->
          @if (itemCategories.length > 0) {
          <select [(ngModel)]="itemCategoryFilter" [ngModelOptions]="{standalone: true}"
            class="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
            <option value="">Todas las categorías</option>
            @for (cat of itemCategories; track cat) {
            <option [value]="cat">{{ cat }}</option>
            }
          </select>
          }
          @if (hasItemFilter) {
          <button type="button" (click)="limpiarFiltroItems()"
            class="px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors whitespace-nowrap">
            Limpiar
          </button>
          }
        </div>
        <!-- Aviso: con filtro activo el reordenamiento se deshabilita -->
        @if (hasItemFilter) {
        <p class="mb-2 text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
          <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          Mostrando {{ visibleItemIndices.length }} de {{ items.length }} ítems. El reordenamiento por arrastre está deshabilitado mientras filtras.
        </p>
        }
        }

        <!-- Items list (reordenable con drag & drop, #16; deshabilitado con filtro activo) -->
        <div formArrayName="items" class="space-y-2" cdkDropList cdkDropListLockAxis="y"
          [cdkDropListDisabled]="hasItemFilter"
          (cdkDropListDropped)="dropItem($event)">
          @for (i of visibleItemIndices; track i) {
          <div [formGroupName]="i" cdkDrag [cdkDragDisabled]="hasItemFilter"
            class="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900">

            <!-- alias del control para reusar el markup existente -->
            @let item = items.at(i);

            <!-- Placeholder mientras se arrastra -->
            <div *cdkDragPlaceholder
              class="h-12 rounded-lg border-2 border-dashed border-brand-300 dark:border-brand-700 bg-brand-50/50 dark:bg-brand-900/10"></div>

            <!-- Compact row header -->
            <div class="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              (click)="toggleItemExpand(i)">
              <!-- Handle de arrastre (⋮⋮) -->
              <span cdkDragHandle (click)="$event.stopPropagation()"
                class="shrink-0 -ml-1 p-1 rounded cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400 transition-colors"
                title="Arrastrar para reordenar">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/>
                  <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
                  <circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/>
                </svg>
              </span>

              <!-- Type badge -->
              @if (item.get('itemType')?.value === 'product') {
              <span class="shrink-0 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded">Producto</span>
              }
              @if (item.get('itemType')?.value === 'service_resource') {
              <span class="shrink-0 px-2 py-0.5 text-xs font-medium bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 rounded">Servicio · Recurso</span>
              }
              @if (item.get('itemType')?.value === 'service_consulting') {
              <span class="shrink-0 px-2 py-0.5 text-xs font-medium bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 rounded">Servicio · Consultoría</span>
              }
              @if (item.get('itemType')?.value === 'bundle') {
              <span class="shrink-0 px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded">Bundle</span>
              }
              @if (item.get('itemType')?.value === 'custom') {
              <span class="shrink-0 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 rounded">Personalizado</span>
              }
              <!-- (INC34) Origen: línea proveniente de un paquete expandido -->
              @if (item.get('bundleId')?.value && item.get('itemType')?.value !== 'bundle') {
              <span class="shrink-0 px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded truncate max-w-[10rem]"
                [title]="'Parte del paquete: ' + bundleNameById(item.get('bundleId')?.value)">
                {{ bundleNameById(item.get('bundleId')?.value) }}
              </span>
              }

              <!-- Name + código -->
              <span class="flex-1 min-w-0 text-sm font-medium text-gray-900 dark:text-white truncate">
                {{ item.get('itemName')?.value || 'Sin nombre' }}
                @if (item.get('itemCode')?.value) {
                <span class="ml-1 text-xs font-normal text-gray-400 dark:text-gray-500">· {{ item.get('itemCode')?.value }}</span>
                }
                <!-- (INC33) Variante elegida en la línea -->
                @if (itemVariantName(i)) {
                <span class="ml-1 inline-flex items-center px-1.5 py-0.5 text-[11px] font-normal bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 rounded">
                  {{ itemVariantName(i) }}
                </span>
                }
              </span>

              <!-- Qty × Price -->
              <span class="hidden sm:block text-xs text-gray-500 dark:text-gray-400 shrink-0">
                {{ item.get('quantity')?.value }} × {{ currencySymbol }} {{ item.get('unitPrice')?.value | number:'1.2-2' }}
              </span>

              <!-- Descuento por regla vigente (Inc 40) -->
              @if (hasItemDiscountRule(i)) {
              <span class="hidden sm:inline-flex shrink-0 items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-success-50 text-success-700 dark:bg-success-500/10 dark:text-success-300 rounded"
                [title]="itemDiscountRule(i)?.message || 'Descuento aplicado'">
                <svg class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                -{{ currencySymbol }} {{ (itemDiscountRule(i)?.discountAmount || 0) | number:'1.2-2' }}
              </span>
              }

              <!-- Subtotal -->
              <span class="text-sm font-semibold text-gray-900 dark:text-white shrink-0">
                {{ currencySymbol }} {{ calculateItemSubtotal(i) | number:'1.2-2' }}
              </span>

              <!-- Chevron -->
              <svg class="w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200"
                [class.rotate-180]="isItemExpanded(i)"
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>

              <!-- Delete -->
              <button type="button" (click)="$event.stopPropagation(); removeItem(i)"
                class="shrink-0 p-1 rounded text-gray-400 hover:text-error-500 hover:bg-error-50 dark:hover:bg-error-500/10 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- Expanded edit form -->
            @if (isItemExpanded(i)) {
            <div class="border-t border-gray-200 dark:border-gray-700 px-4 py-4 space-y-3 bg-gray-50 dark:bg-gray-800/30">

              <!-- Item Type toggle (Incidente 24): Productos / Servicios (Recursos · Consultoría) / Personalizado -->
              <div class="flex flex-wrap gap-x-4 gap-y-2">
                <label class="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" formControlName="itemType" value="product" (change)="onItemTypeChange(i)"
                    class="w-3.5 h-3.5 text-brand-600 focus:ring-brand-500">
                  <span class="text-xs text-gray-700 dark:text-gray-300">Productos</span>
                </label>
                <label class="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" formControlName="itemType" value="service_resource" (change)="onItemTypeChange(i)"
                    class="w-3.5 h-3.5 text-brand-600 focus:ring-brand-500">
                  <span class="text-xs text-gray-700 dark:text-gray-300">Servicios · Recursos</span>
                </label>
                <label class="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" formControlName="itemType" value="service_consulting" (change)="onItemTypeChange(i)"
                    class="w-3.5 h-3.5 text-brand-600 focus:ring-brand-500">
                  <span class="text-xs text-gray-700 dark:text-gray-300">Servicios · Consultoría</span>
                </label>
                <label class="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" formControlName="itemType" value="custom" (change)="onItemTypeChange(i)"
                    class="w-3.5 h-3.5 text-brand-600 focus:ring-brand-500">
                  <span class="text-xs text-gray-700 dark:text-gray-300">Personalizado</span>
                </label>
              </div>

              <!-- (INC_CRM_005 (5) inc41) Subtipo de Productos de la línea: acota el buscador del catálogo. -->
              @if (item.get('itemType')?.value === 'product') {
              <div class="flex gap-4">
                <label class="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" [name]="'itemProductKind' + i" [checked]="productKindFor(i) === 'inventory'"
                    (change)="setProductKindFor(i, 'inventory')"
                    class="w-3.5 h-3.5 text-brand-600 focus:ring-brand-500">
                  <span class="text-xs text-gray-700 dark:text-gray-300">Con Inventario</span>
                </label>
                <label class="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" [name]="'itemProductKind' + i" [checked]="productKindFor(i) === 'no_inventory'"
                    (change)="setProductKindFor(i, 'no_inventory')"
                    class="w-3.5 h-3.5 text-brand-600 focus:ring-brand-500">
                  <span class="text-xs text-gray-700 dark:text-gray-300">Sin Inventario</span>
                </label>
              </div>
              }

              <!-- Product typeahead: producto (requerido) o servicio (productId opcional autocompleta código/categoría) -->
              @if (isProductLikeType(item.get('itemType')?.value)) {
              <div class="relative">
                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{{ item.get('itemType')?.value === 'product' ? 'Producto' : 'Producto (opcional)' }}</label>
                <input type="text"
                  [value]="itemProductSearchTerms[i] || ''"
                  (input)="onItemProductSearchInput(i, $any($event.target).value)"
                  (keydown.enter)="onItemProductSearchEnter(i, $event)"
                  (blur)="closeItemProductDropdown(i)"
                  placeholder="Buscar producto... (Enter para ver todos)"
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"/>
                @if (showItemProductDropdown[i] && getFilteredProductsForItem(i).length > 0) {
                <div class="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900 max-h-40 overflow-y-auto">
                  @for (p of getFilteredProductsForItem(i); track p.id) {
                  <button type="button" (mousedown)="selectProductForItem(i, p)"
                    class="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <span class="block text-sm font-medium text-gray-900 dark:text-white">{{ p.name }}</span>
                    <span class="block text-xs text-gray-500 dark:text-gray-400">{{ p.sku }} · {{ currencySymbol }} {{ p.price | number:'1.2-2' }}</span>
                  </button>
                  }
                </div>
                }
              </div>
              }

              <!--
                (INC33) Selector de variante. Solo aparece si el producto elegido TIENE variantes
                activas; si no tiene, no se renderiza nada (sin ruido visual). Al elegir una, el
                precio efectivo y el SKU se autocompletan y el backend resuelve el precio canónico.
              -->
              @if (item.get('itemType')?.value === 'product' && hasVariants(i)) {
              <div>
                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Variante</label>
                <select [ngModel]="item.get('variantId')?.value" [ngModelOptions]="{standalone: true}"
                  (ngModelChange)="onItemVariantChange(i, $event)"
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
                  <option [ngValue]="null">Sin variante (producto base)</option>
                  @for (v of variantsFor(i); track v.id) {
                  <option [ngValue]="v.id">{{ variantLabel(v) }} · {{ currencySymbol }} {{ v.effectivePrice | number:'1.2-2' }}</option>
                  }
                </select>
              </div>
              }

              <!-- Bundle typeahead -->
              @if (item.get('itemType')?.value === 'bundle') {
              <div class="relative">
                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Bundle</label>
                <input type="text"
                  [value]="itemBundleSearchTerms[i] || ''"
                  (input)="onItemBundleSearchInput(i, $any($event.target).value)"
                  (keydown.enter)="onItemBundleSearchEnter(i, $event)"
                  (blur)="closeItemBundleDropdown(i)"
                  placeholder="Buscar bundle... (Enter para ver todos)"
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"/>
                @if (showItemBundleDropdown[i] && getFilteredBundlesForItem(i).length > 0) {
                <div class="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900 max-h-40 overflow-y-auto">
                  @for (b of getFilteredBundlesForItem(i); track b.id) {
                  <button type="button" (mousedown)="selectBundleForItem(i, b)"
                    class="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <span class="block text-sm font-medium text-gray-900 dark:text-white">{{ b.name }}</span>
                    <span class="block text-xs text-gray-500 dark:text-gray-400">{{ b.sku }} · {{ currencySymbol }} {{ (b.bundlePrice || 0) | number:'1.2-2' }}</span>
                  </button>
                  }
                </div>
                }
              </div>
              }

              <!-- Name + Qty + Unit + Price grid -->
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div class="col-span-2">
                  <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nombre *</label>
                  <input type="text" formControlName="itemName"
                    class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    [class.border-error-500]="hasItemError(i, 'itemName')" placeholder="Nombre del ítem"/>
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Cantidad *</label>
                  <input type="number" formControlName="quantity" step="0.01" min="0.01"
                    (change)="onItemQuantityChange(i)"
                    class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    [class.border-error-500]="hasItemError(i, 'quantity')"/>
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Unidad</label>
                  <input type="text" formControlName="unit" maxlength="50"
                    class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    placeholder="und, hr..."/>
                </div>
                <div class="col-span-2">
                  <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Precio Unitario *</label>
                  <input type="number" formControlName="unitPrice" step="0.01" min="0"
                    (change)="onItemUnitPriceChange(i)"
                    class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    [class.border-error-500]="hasItemError(i, 'unitPrice')"/>
                  @if (tierAppliedItems[i]) {
                  <p class="mt-1 text-[11px] text-teal-600 dark:text-teal-400 inline-flex items-center gap-1">
                    <svg class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                    </svg>
                    Precio por volumen aplicado según la cantidad
                  </p>
                  }
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Desc. %</label>
                  <input type="number" formControlName="discountPercent" step="0.01" min="0" max="100"
                    class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"/>
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Desc. Fijo</label>
                  <input type="number" formControlName="discountAmount" step="0.01" min="0"
                    class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"/>
                </div>
                <!-- Código (V75): para productos el backend lo autocompleta con el sku si va vacío -->
                <div>
                  <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Código</label>
                  <input type="text" formControlName="itemCode" maxlength="100"
                    class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    placeholder="SKU / código (opcional)"/>
                </div>
                <!-- Impuesto % por línea (V75) -->
                <div>
                  <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Impuesto %</label>
                  <input type="number" formControlName="itemTaxRate" step="0.01" min="0" max="100"
                    class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="Impuesto de la línea (opcional)"/>
                </div>
                <!-- Categoría (V76): para productos el backend la autocompleta si va vacía -->
                <div class="col-span-2">
                  <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Categoría</label>
                  <input type="text" formControlName="category" maxlength="150"
                    class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    placeholder="Categoría (opcional)"/>
                </div>
              </div>

              <!-- Description -->
              <div>
                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Descripción</label>
                <textarea formControlName="description" rows="2"
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  placeholder="Descripción del ítem"></textarea>
              </div>

              <!--
                (INC32) Reglas de descuento visibles y gobernables. El automático (Inc 40) sigue
                siendo el modo por defecto; aquí se hace explícito y se puede forzar otra regla,
                aplicar un cupón o desactivarlo. El monto resultante siempre va a "Desc. Fijo".
              -->
              @if (item.get('itemType')?.value === 'product' && item.get('productId')?.value) {
              <div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 px-3 py-2.5 space-y-2.5">
                <div class="flex items-center gap-1.5">
                  <svg class="w-3.5 h-3.5 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 12V7a4 4 0 014-4z"/>
                  </svg>
                  <span class="text-xs font-semibold text-gray-700 dark:text-gray-300">Descuento de la línea</span>
                </div>

                <!-- Selector explícito de regla -->
                <div>
                  <select [ngModel]="discountChoiceOf(i)" [ngModelOptions]="{standalone: true}"
                    (ngModelChange)="onItemDiscountChoiceChange(i, $event)"
                    class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
                    <option value="auto">Automático (mejor regla vigente)</option>
                    @for (r of applicableRulesFor(i); track r.id) {
                    <option [value]="'rule:' + r.id">
                      {{ ruleLabel(r) }}{{ isRuleOutOfQuantityRange(i, r) ? ' (fuera de rango de cantidad)' : '' }}
                    </option>
                    }
                    <option value="none">Sin descuento</option>
                    <!-- Solo visible mientras haya un cupón aplicado: elegir otra opción lo quita. -->
                    @if (hasItemCoupon(i)) {
                    <option value="coupon">Cupón aplicado</option>
                    }
                  </select>
                  @if (applicableRulesFor(i).length === 0) {
                  <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Este producto no tiene reglas de descuento configuradas.</p>
                  }
                </div>

                <!-- Cupón -->
                <div>
                  <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Cupón</label>
                  <div class="flex items-center gap-2">
                    <!-- readonly (no [disabled]): un control deshabilitado sale del value del form -->
                    <input type="text" [(ngModel)]="itemCouponCode[i]" [ngModelOptions]="{standalone: true}"
                      [readOnly]="hasItemCoupon(i)"
                      placeholder="Código de cupón"
                      class="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent read-only:opacity-60"/>
                    @if (hasItemCoupon(i)) {
                    <button type="button" (click)="quitarCuponLinea(i)"
                      class="shrink-0 px-3 py-2 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      Quitar
                    </button>
                    } @else {
                    <button type="button" (click)="aplicarCuponLinea(i)"
                      [disabled]="applyingCoupon[i] || !(itemCouponCode[i] || '').trim()"
                      class="shrink-0 px-3 py-2 text-xs font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                      {{ applyingCoupon[i] ? 'Aplicando...' : 'Aplicar' }}
                    </button>
                    }
                  </div>
                  @if (itemCouponError[i]) {
                  <p class="mt-1 text-[11px] text-error-500">{{ itemCouponError[i] }}</p>
                  }
                </div>
              </div>
              }

              <!-- Descuento por regla vigente aplicado automáticamente (Inc 40) -->
              @if (hasItemDiscountRule(i)) {
              <div class="rounded-lg border border-success-200 dark:border-success-500/30 bg-success-50 dark:bg-success-500/10 px-3 py-2">
                <div class="flex items-start gap-2">
                  <svg class="w-4 h-4 mt-0.5 shrink-0 text-success-600 dark:text-success-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs font-medium text-success-700 dark:text-success-300">{{ itemDiscountRule(i)?.message || 'Descuento aplicado automáticamente' }}</p>
                    <p class="text-[11px] text-success-600 dark:text-success-400 mt-0.5">
                      Descuento: {{ currencySymbol }} {{ (itemDiscountRule(i)?.discountAmount || 0) | number:'1.2-2' }}
                      · Total línea: {{ currencySymbol }} {{ (itemDiscountRule(i)?.finalTotal || 0) | number:'1.2-2' }}
                    </p>
                  </div>
                </div>
              </div>
              }

              <!-- Row subtotal -->
              <div class="flex justify-end text-sm font-semibold text-gray-900 dark:text-white">
                Subtotal: {{ currencySymbol }} {{ calculateItemSubtotal(i) | number:'1.2-2' }}
              </div>
            </div>
            }
          </div>
          }
        </div>

        <!-- Sin resultados del filtro (hay ítems, pero ninguno coincide) -->
        @if (items.length > 0 && visibleItemIndices.length === 0) {
        <div class="text-center py-6 text-sm text-gray-500 dark:text-gray-400">
          Ningún ítem coincide con el filtro.
          <button type="button" (click)="limpiarFiltroItems()" class="ml-1 font-medium text-brand-600 dark:text-brand-400 hover:underline">Limpiar filtro</button>
        </div>
        }

        <!-- Add item form panel -->
        @if (showAddItemForm) {
        <div class="mt-3 border border-brand-200 dark:border-brand-700/50 rounded-xl bg-brand-50/30 dark:bg-brand-900/10 p-4 space-y-3">
          <div class="flex items-center justify-between">
            <span class="text-sm font-semibold text-gray-900 dark:text-white">Nuevo Ítem</span>
            <button type="button" (click)="cancelAddItemForm()"
              class="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Type toggle (Incidente 24): Productos / Servicios / Personalizado -->
          <div class="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg w-fit">
            <button type="button" (click)="addFormItemType='product'; onAddFormTypeChange()"
              [class]="addFormItemType === 'product' ? 'px-3 py-1 text-xs font-medium rounded bg-white dark:bg-gray-700 text-brand-600 dark:text-brand-400 shadow-sm' : 'px-3 py-1 text-xs text-gray-600 dark:text-gray-400'">
              Productos
            </button>
            <button type="button" (click)="selectServiceAddType()"
              [class]="isServiceAddType ? 'px-3 py-1 text-xs font-medium rounded bg-white dark:bg-gray-700 text-brand-600 dark:text-brand-400 shadow-sm' : 'px-3 py-1 text-xs text-gray-600 dark:text-gray-400'">
              Servicios
            </button>
            <button type="button" (click)="addFormItemType='custom'; onAddFormTypeChange()"
              [class]="addFormItemType === 'custom' ? 'px-3 py-1 text-xs font-medium rounded bg-white dark:bg-gray-700 text-brand-600 dark:text-brand-400 shadow-sm' : 'px-3 py-1 text-xs text-gray-600 dark:text-gray-400'">
              Personalizado
            </button>
          </div>

          <!-- Subtipo de Servicios: Recursos / Consultoría -->
          @if (isServiceAddType) {
          <div class="flex gap-4">
            <label class="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="addFormServiceSubtype" [checked]="addFormItemType === 'service_resource'"
                (change)="selectServiceAddSubtype('service_resource')"
                class="w-3.5 h-3.5 text-brand-600 focus:ring-brand-500">
              <span class="text-xs text-gray-700 dark:text-gray-300">Recursos</span>
            </label>
            <label class="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="addFormServiceSubtype" [checked]="addFormItemType === 'service_consulting'"
                (change)="selectServiceAddSubtype('service_consulting')"
                class="w-3.5 h-3.5 text-brand-600 focus:ring-brand-500">
              <span class="text-xs text-gray-700 dark:text-gray-300">Consultoría</span>
            </label>
          </div>
          }

          <!-- (INC_CRM_005 (5) inc41) Subtipo de Productos: Con Inventario / Sin Inventario.
               Espejo de Recursos/Consultoría: acota el buscador del catálogo, no cambia el
               itemType que se envía ('product' en ambos casos). -->
          @if (addFormItemType === 'product') {
          <div class="flex gap-4">
            <label class="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="addFormProductKind" [checked]="addFormProductKind === 'inventory'"
                (change)="setAddFormProductKind('inventory')"
                class="w-3.5 h-3.5 text-brand-600 focus:ring-brand-500">
              <span class="text-xs text-gray-700 dark:text-gray-300">Con Inventario</span>
            </label>
            <label class="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="addFormProductKind" [checked]="addFormProductKind === 'no_inventory'"
                (change)="setAddFormProductKind('no_inventory')"
                class="w-3.5 h-3.5 text-brand-600 focus:ring-brand-500">
              <span class="text-xs text-gray-700 dark:text-gray-300">Sin Inventario</span>
            </label>
          </div>
          }

          <!-- Product typeahead for add form: requerido en Productos, opcional en Servicios -->
          @if (addFormItemType === 'product' || isServiceAddType) {
          <div class="relative">
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{{ addFormItemType === 'product' ? 'Producto' : 'Producto (opcional)' }}</label>
            <input type="text"
              [value]="addFormProductSearch"
              (input)="onAddFormProductSearchInput($any($event.target).value)"
              (keydown.enter)="onAddFormProductSearchEnter($event)"
              (blur)="closeAddFormProductDropdown()"
              placeholder="Buscar producto... (Enter para ver todos)"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"/>
            @if (showAddFormProductDropdown && filteredProductsForAddForm.length > 0) {
            <div class="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900 max-h-40 overflow-y-auto">
              @for (p of filteredProductsForAddForm; track p.id) {
              <button type="button" (mousedown)="selectProductForAddForm(p)"
                class="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <span class="block text-sm font-medium text-gray-900 dark:text-white">{{ p.name }}</span>
                <span class="block text-xs text-gray-500 dark:text-gray-400">{{ p.sku }} · {{ currencySymbol }} {{ p.price | number:'1.2-2' }}</span>
              </button>
              }
            </div>
            }
          </div>
          }

          <!-- (INC33) Variante del producto elegido (solo si tiene variantes activas) -->
          @if (addFormItemType === 'product' && addFormVariants.length > 0) {
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Variante</label>
            <select [ngModel]="addFormVariantId" [ngModelOptions]="{standalone: true}"
              (ngModelChange)="onAddFormVariantChange($event)"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
              <option [ngValue]="null">Sin variante (producto base)</option>
              @for (v of addFormVariants; track v.id) {
              <option [ngValue]="v.id">{{ variantLabel(v) }} · {{ currencySymbol }} {{ v.effectivePrice | number:'1.2-2' }}</option>
              }
            </select>
          </div>
          }

          <!-- Name + Qty + Unit + Price grid -->
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div class="col-span-2">
              <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nombre *</label>
              <input type="text" [(ngModel)]="addFormItemName" [ngModelOptions]="{standalone: true}"
                class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="Nombre del ítem"/>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Cantidad *</label>
              <input type="number" [(ngModel)]="addFormQuantity" [ngModelOptions]="{standalone: true}" step="0.01" min="0.01"
                (change)="onAddFormQuantityChange()"
                class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"/>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Unidad</label>
              <input type="text" [(ngModel)]="addFormUnit" [ngModelOptions]="{standalone: true}" maxlength="50"
                class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="und, hr..."/>
            </div>
            <div class="col-span-2">
              <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Precio Unitario *</label>
              <input type="number" [(ngModel)]="addFormUnitPrice" [ngModelOptions]="{standalone: true}" step="0.01" min="0"
                (change)="onAddFormUnitPriceChange()"
                class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"/>
              @if (addFormTierApplied) {
              <p class="mt-1 text-[11px] text-teal-600 dark:text-teal-400 inline-flex items-center gap-1">
                <svg class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                </svg>
                Precio por volumen aplicado según la cantidad
              </p>
              }
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Desc. %</label>
              <input type="number" [(ngModel)]="addFormDiscountPercent" [ngModelOptions]="{standalone: true}" step="0.01" min="0" max="100"
                class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"/>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Desc. Fijo</label>
              <input type="number" [(ngModel)]="addFormDiscountAmount" [ngModelOptions]="{standalone: true}" step="0.01" min="0"
                class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"/>
            </div>
            <!-- Código (V75) -->
            <div>
              <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Código</label>
              <input type="text" [(ngModel)]="addFormItemCode" [ngModelOptions]="{standalone: true}" maxlength="100"
                class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="SKU / código (opcional)"/>
            </div>
            <!-- Categoría (V76) -->
            <div class="col-span-2">
              <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Categoría</label>
              <input type="text" [(ngModel)]="addFormCategory" [ngModelOptions]="{standalone: true}" maxlength="150"
                class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="Categoría (opcional)"/>
            </div>
          </div>

          <!-- Descuento por regla vigente aplicado automáticamente (Inc 40) -->
          @if (addFormDiscountRule?.applied) {
          <div class="rounded-lg border border-success-200 dark:border-success-500/30 bg-success-50 dark:bg-success-500/10 px-3 py-2">
            <div class="flex items-start gap-2">
              <svg class="w-4 h-4 mt-0.5 shrink-0 text-success-600 dark:text-success-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <div class="min-w-0 flex-1">
                <p class="text-xs font-medium text-success-700 dark:text-success-300">{{ addFormDiscountRule?.message || 'Descuento aplicado automáticamente' }}</p>
                <p class="text-[11px] text-success-600 dark:text-success-400 mt-0.5">
                  Descuento: {{ currencySymbol }} {{ (addFormDiscountRule?.discountAmount || 0) | number:'1.2-2' }}
                  · Total línea: {{ currencySymbol }} {{ (addFormDiscountRule?.finalTotal || 0) | number:'1.2-2' }}
                </p>
              </div>
            </div>
          </div>
          }

          <!-- Description -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Descripción</label>
            <textarea [(ngModel)]="addFormDescription" [ngModelOptions]="{standalone: true}" rows="2"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              placeholder="Descripción del ítem (opcional)"></textarea>
          </div>

          <!-- Confirm/Cancel -->
          <div class="flex items-center justify-end gap-2 pt-1">
            <button type="button" (click)="cancelAddItemForm()"
              class="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
              Cancelar
            </button>
            <button type="button" (click)="confirmAddItem()"
              [disabled]="!addFormItemName.trim() || addFormQuantity <= 0"
              class="px-4 py-1.5 text-xs font-medium bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              Agregar a la lista
            </button>
          </div>
        </div>
        }

        <!--
          (INC34) Panel "Añadir paquete". Al confirmar, el paquete se EXPANDE en N líneas de ítem
          normales de tipo Producto (una por componente), con el precio del paquete repartido como
          descuento proporcional al importe de cada línea. Los tipos de ítem no se tocan.
        -->
        @if (showAddBundleForm) {
        <div class="mt-3 border border-purple-200 dark:border-purple-700/50 rounded-xl bg-purple-50/30 dark:bg-purple-900/10 p-4 space-y-3">
          <div class="flex items-center justify-between">
            <span class="text-sm font-semibold text-gray-900 dark:text-white">Añadir paquete</span>
            <button type="button" (click)="cerrarAgregarPaquete()"
              class="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <p class="text-[11px] text-gray-500 dark:text-gray-400">
            El paquete se agregará como una línea por cada producto que lo compone, con el precio del paquete repartido entre ellas.
          </p>

          <div class="relative">
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Paquete</label>
            <input type="text"
              [value]="bundleSearch"
              (input)="onBundleSearchInput($any($event.target).value)"
              (keydown.enter)="onBundleSearchEnter($event)"
              (blur)="closeBundleDropdown()"
              placeholder="Buscar paquete... (Enter para ver todos)"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"/>
            @if (showBundleDropdown && filteredBundlesForPanel.length > 0) {
            <div class="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900 max-h-40 overflow-y-auto">
              @for (b of filteredBundlesForPanel; track b.id) {
              <button type="button" (mousedown)="seleccionarPaquete(b)"
                class="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <span class="block text-sm font-medium text-gray-900 dark:text-white">{{ b.name }}</span>
                <span class="block text-xs text-gray-500 dark:text-gray-400">{{ b.sku }} · {{ currencySymbol }} {{ (b.bundlePrice || 0) | number:'1.2-2' }}</span>
              </button>
              }
            </div>
            }
            @if (bundles.length === 0) {
            <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">No hay paquetes activos configurados.</p>
            }
          </div>

          <div class="flex items-center justify-end gap-2 pt-1">
            <button type="button" (click)="cerrarAgregarPaquete()"
              class="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
              Cancelar
            </button>
            <button type="button" (click)="confirmAddBundle()"
              [disabled]="!selectedBundleId || addingBundle"
              class="px-4 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {{ addingBundle ? 'Añadiendo...' : 'Añadir a la lista' }}
            </button>
          </div>
        </div>
        }

        <!-- Empty state -->
        @if (items.length === 0 && !showAddItemForm && !showAddBundleForm) {
        <div class="mt-2 text-center py-10 px-4 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
          <svg class="mx-auto w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
          </svg>
          <p class="text-sm font-medium text-gray-700 dark:text-gray-200">Aún no agregas productos</p>
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400 max-w-sm mx-auto">Busca un producto o agrega un ítem personalizado para empezar a construir la cotización.</p>
          <div class="mt-4 flex items-center justify-center gap-2">
            <button type="button" (click)="openImportItemsModal()"
              class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"/>
              </svg>
              Importar ítems
            </button>
            <!-- (INC34) Alta de paquete también desde el estado vacío -->
            <button type="button" (click)="abrirAgregarPaquete()"
              class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-purple-300 dark:border-purple-700/60 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
              </svg>
              Añadir paquete
            </button>
            <button type="button" (click)="openAddItemForm()"
              class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              Agregar ítem
            </button>
          </div>
        </div>
        }

        <!-- Add another item / paquete (when items exist and both panels hidden) -->
        @if (items.length > 0 && !showAddItemForm && !showAddBundleForm) {
        <div class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button type="button" (click)="openAddItemForm()"
            class="w-full py-2 text-xs font-medium text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-brand-400 hover:text-brand-500 transition-colors">
            + Agregar otro ítem
          </button>
          <button type="button" (click)="abrirAgregarPaquete()"
            class="w-full py-2 text-xs font-medium text-purple-600 dark:text-purple-400 border border-dashed border-purple-300 dark:border-purple-700/60 rounded-lg hover:border-purple-500 hover:text-purple-700 dark:hover:text-purple-300 transition-colors">
            + Añadir paquete
          </button>
        </div>
        }
      </div>

      <!-- Card: Distribución por categorías (V76) — solo lectura, calculada en el front -->
      <!-- (Incidente 14) Solo se muestra si hay al menos una categoría real definida; si ningún
           ítem tiene categoría, se oculta para no mostrar un único bloque "Sin categoría". -->
      @if (hasRealCategories) {
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
        <h2 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
          Distribución por categorías
        </h2>
        <div class="space-y-1.5">
          @for (group of categoryDistribution; track group.category) {
          <div class="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/40">
            <div class="flex items-center gap-2 min-w-0">
              <span class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ group.category }}</span>
              <span class="shrink-0 px-1.5 py-0.5 text-[11px] font-medium rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                {{ group.count }} {{ group.count === 1 ? 'ítem' : 'ítems' }}
              </span>
            </div>
            <span class="shrink-0 text-sm font-semibold text-gray-900 dark:text-white">
              {{ currencySymbol }} {{ group.subtotal | number:'1.2-2' }}
            </span>
          </div>
          }
        </div>
      </div>
      }
    </div>
  }

  <!-- ============ PASO 3 — TOTALES Y CONDICIONES ============ -->
  @if (currentStep === 3) {
    <!-- ≥ xl: distribución real y balanceada en 2 columnas internas (izq. = impuestos/condiciones/aprobación, -->
    <!-- der. = presentación/adjuntos/términos/totales). items-start evita columnas estiradas. -->
    <div class="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">

      <!-- Columna izquierda -->
      <div class="space-y-4 min-w-0">

      <!-- Tax & Discount Config card -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-3">
        <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Impuesto y Descuento</h3>

        <!-- Tax toggle -->
        <div class="flex items-center justify-between">
          <label for="taxEnabled" class="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">Aplicar Impuesto</label>
          <input type="checkbox" formControlName="taxEnabled" id="taxEnabled"
            class="w-4 h-4 text-brand-600 focus:ring-brand-500 rounded cursor-pointer"/>
        </div>

        @if (form.get('taxEnabled')?.value) {
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Etiqueta</label>
            <input type="text" formControlName="taxLabel" maxlength="20"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              placeholder="IGV, IVA..."/>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Tasa (0–1)</label>
            <input type="number" formControlName="taxRate" step="0.01" min="0" max="1"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              placeholder="0.18"/>
          </div>
        </div>
        }

        <div class="border-t border-gray-100 dark:border-gray-800"></div>

        <!-- Global discount -->
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Descuento Global</label>
          <div class="flex gap-2">
            <!-- (Incidente 2b) Número simple sin flechas: appearance:textfield + spin-button reset -->
            <input type="number" formControlName="globalDiscount" min="0"
              class="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent appearance-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              [class.border-error-500]="hasError('globalDiscount')"/>
            <!-- (Incidente 2a) El tipo monetario usa el símbolo/código de la moneda de la cotización -->
            <select formControlName="globalDiscountType"
              (change)="form.get('globalDiscount')?.updateValueAndValidity()"
              class="px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
              <option value="fixed">{{ currencySymbol }}</option>
              <option value="percent">%</option>
            </select>
          </div>
          <!-- (Incidente 2c) Mensajes de validación: positivo y, si es %, máximo 100 -->
          @if (hasError('globalDiscount')) {
          <p class="mt-1 text-xs text-error-600">{{ getError('globalDiscount') }}</p>
          } @else {
          <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
            {{ form.get('globalDiscountType')?.value === 'percent' ? 'Porcentaje entre 0 y 100.' : 'Monto en ' + currencySymbol + ' (no negativo).' }}
          </p>
          }
        </div>

      </div>

      <!-- Condiciones comerciales (V74) -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-3">
        <div>
          <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Condiciones comerciales</h3>
          <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Todos los campos son opcionales; aparecen en el documento si los completas.</p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Forma de pago -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Forma de pago</label>
            <select formControlName="paymentType"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
              <option [ngValue]="null">— Sin especificar —</option>
              <option value="CONTADO">Contado</option>
              <option value="CREDITO">Crédito</option>
            </select>
          </div>

          <!-- Plazo de crédito (solo si es Crédito) -->
          @if (form.get('paymentType')?.value === 'CREDITO') {
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Plazo de crédito (días)</label>
            <input type="number" formControlName="creditTermDays" step="1" min="0"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              [class.border-error-500]="hasError('creditTermDays')" placeholder="30"/>
            <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Días que tiene el cliente para pagar tras la entrega.</p>
            @if (hasError('creditTermDays')) {
            <p class="mt-1 text-xs text-error-600">{{ getError('creditTermDays') }}</p>
            }
          </div>

          <!-- Tasa de interés (%) — solo crédito -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Tasa de interés (%)</label>
            <input type="number" formControlName="interestRatePercent" step="0.01" min="0" max="100"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              [class.border-error-500]="hasError('interestRatePercent')" placeholder="0"/>
            <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Interés aplicado al saldo financiado. Déjalo vacío o en 0 si no hay financiamiento.</p>
            @if (hasError('interestRatePercent')) {
            <p class="mt-1 text-xs text-error-600">{{ getError('interestRatePercent') }}</p>
            }
          </div>
          }

          <!-- Anticipo (%) -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Anticipo (%)</label>
            <input type="number" formControlName="advancePercent" step="0.01" min="0" max="100"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              [class.border-error-500]="hasError('advancePercent')" placeholder="0"/>
            <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Porcentaje del total a pagar por adelantado.</p>
            @if (hasError('advancePercent')) {
            <p class="mt-1 text-xs text-error-600">{{ getError('advancePercent') }}</p>
            }
          </div>

          <!-- Tipo de cambio (INC_CRM_005 (5) inc44): se elige del catálogo de monedas
               (las mismas tasas del módulo de Productos) en vez de teclearlo a ciegas.
               "Otro (manual)" conserva la captura libre para tasas pactadas. -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Tipo de cambio</label>
            <select [ngModel]="exchangeRateSelection" [ngModelOptions]="{standalone: true}"
              (ngModelChange)="onExchangeRateSelectionChange($event)"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
              <option value="">— Sin tipo de cambio —</option>
              @for (o of exchangeRateOptions; track o.currencyId) {
              <option [value]="o.currencyId">{{ o.label }}</option>
              }
              <option value="manual">Otro (manual)</option>
            </select>
            @if (exchangeRateSelection === 'manual') {
            <input type="number" formControlName="exchangeRate" step="0.0001" min="0"
              class="mt-2 w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              [class.border-error-500]="hasError('exchangeRate')" placeholder="3.75"/>
            }
            @if (!form.get('currencyId')?.value) {
            <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Elige primero la moneda de la cotización (paso 1) para ver las tasas del catálogo.</p>
            } @else if (exchangeRateOptions.length === 0) {
            <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">El catálogo de monedas no tiene tasas configuradas; usa "Otro (manual)".</p>
            } @else {
            <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Tasas del catálogo de monedas (módulo Productos). Solo si la moneda difiere de la base del negocio.</p>
            }
            @if (hasError('exchangeRate')) {
            <p class="mt-1 text-xs text-error-600">{{ getError('exchangeRate') }}</p>
            }
          </div>

          <!-- Plazo de entrega -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Plazo de entrega</label>
            <input type="text" formControlName="deliveryTime" maxlength="500"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              placeholder="Ej. 15 días hábiles"/>
          </div>

          <!-- Lugar de entrega -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Lugar de entrega</label>
            <input type="text" formControlName="deliveryPlace" maxlength="500"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              placeholder="Ej. Almacén del cliente, Lima"/>
          </div>

          <!-- Garantía -->
          <div class="sm:col-span-2">
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Garantía</label>
            <input type="text" formControlName="warranty" maxlength="500"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              placeholder="Ej. 12 meses contra defectos de fábrica"/>
          </div>
        </div>
      </div>

      <!-- Requiere aprobación interna (movido desde el antiguo paso 4) -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
        <div class="flex items-center justify-between">
          <label for="requiresApproval" class="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">Requiere aprobación interna</label>
          <input type="checkbox" formControlName="requiresApproval" id="requiresApproval"
            class="w-4 h-4 text-brand-600 focus:ring-brand-500 rounded cursor-pointer"/>
        </div>
        @if (form.get('requiresApproval')?.value) {
        <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
          Esta cotización deberá aprobarse internamente antes de poder publicarse o enviarse al cliente.
        </p>
        }
      </div>

      </div><!-- /columna izquierda paso 3 -->

      <!-- Columna derecha -->
      <div class="space-y-4 min-w-0">

      <!-- Opciones de presentación del documento (V76) -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-3">
        <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Opciones de presentación</h3>

        <div class="flex items-center justify-between">
          <label for="groupByCategory" class="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">Agrupar ítems por categoría en PDF</label>
          <input type="checkbox" formControlName="groupByCategory" id="groupByCategory"
            class="w-4 h-4 text-brand-600 focus:ring-brand-500 rounded cursor-pointer"/>
        </div>

        <div class="flex items-center justify-between">
          <label for="showItemCodes" class="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">Mostrar códigos internos</label>
          <input type="checkbox" formControlName="showItemCodes" id="showItemCodes"
            class="w-4 h-4 text-brand-600 focus:ring-brand-500 rounded cursor-pointer"/>
        </div>
      </div>

      <!-- Documentos adjuntos. (Incidente 20) Disponible al crear: el componente real requiere quoteId,
           así que antes del primer guardado se muestra un placeholder con hint. Tras guardar (entrar a
           Revisión persiste la cotización y deja isEditing=true), la sección se habilita automáticamente. -->
      @if (isEditing && quoteId) {
      <app-quote-attachments [quoteId]="quoteId" [compact]="true"></app-quote-attachments>
      } @else {
      <div class="bg-white dark:bg-gray-900 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-5 space-y-2">
        <div class="flex items-center gap-2">
          <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
          </svg>
          <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Documentos adjuntos</h3>
        </div>
        <p class="text-xs text-gray-400 dark:text-gray-500">
          Guarda la cotización para adjuntar documentos (propuesta técnica, condiciones, anexos en PDF/XLSX).
          La sección se habilitará automáticamente tras el primer guardado.
        </p>
      </div>
      }

      <!-- Terms (Términos y Condiciones) -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-3">
        <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Términos y Condiciones</h3>
        <textarea formControlName="termsConditions" rows="4" maxlength="5000"
          class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          placeholder="Términos y condiciones de la cotización..."></textarea>

        <!-- Cláusulas legales (V72) -->
        <div class="border-t border-gray-100 dark:border-gray-800 pt-3">
          <button type="button" (click)="toggleClausesSection()"
            class="flex items-center justify-between w-full group">
            <span class="text-xs font-medium text-gray-600 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors">
              Cláusulas legales
              @if (!showClauses && selectedClauseIds.size > 0) {
              <span class="ml-2 text-gray-400 dark:text-gray-500 font-normal">{{ selectedClauseIds.size }} seleccionada(s)</span>
              }
            </span>
            <svg class="w-3.5 h-3.5 text-gray-400 transition-transform shrink-0" [class.rotate-180]="showClauses" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
          @if (showClauses) {
            @if (clauses.length === 0) {
              <p class="mt-2 text-xs text-gray-400 dark:text-gray-500">
                No hay cláusulas configuradas para tu organización.
              </p>
            } @else {
              <div class="mt-2 space-y-1.5">
                @for (clause of clauses; track clause.id) {
                  <label class="flex items-start gap-2.5 cursor-pointer select-none rounded-lg px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <input type="checkbox"
                      [checked]="isClauseSelected(clause.id)"
                      (change)="toggleClause(clause.id)"
                      class="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 dark:border-gray-600 text-brand-600 focus:ring-brand-500/30">
                    <span class="min-w-0">
                      <span class="block text-sm text-gray-800 dark:text-gray-200">{{ clause.name }}</span>
                      @if (clause.description) {
                        <span class="block text-xs text-gray-400 dark:text-gray-500">{{ clause.description }}</span>
                      }
                    </span>
                  </label>
                }
              </div>
            }
          }
        </div>
      </div>

      <!-- Totals card -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-2">
        <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Totales</h3>
        <div class="flex justify-between text-sm">
          <span class="text-gray-600 dark:text-gray-400">Subtotal</span>
          <span class="font-medium text-gray-900 dark:text-white">{{ currencySymbol }} {{ calculateSubtotal() | number:'1.2-2' }}</span>
        </div>
        @if (form.get('taxEnabled')?.value) {
        <div class="flex justify-between text-sm">
          <span class="text-gray-600 dark:text-gray-400">{{ effectiveTaxLabel }} ({{ (effectiveTaxRate * 100).toFixed(0) }}%)</span>
          <span class="font-medium text-gray-900 dark:text-white">{{ currencySymbol }} {{ calculateTax() | number:'1.2-2' }}</span>
        </div>
        }
        @if ((form.get('globalDiscount')?.value || 0) > 0) {
        <div class="flex justify-between text-sm">
          <span class="text-gray-600 dark:text-gray-400">Descuento{{ form.get('globalDiscountType')?.value === 'percent' ? ' (' + form.get('globalDiscount')?.value + '%)' : '' }}</span>
          <span class="font-medium text-error-600 dark:text-error-400">− {{ currencySymbol }} {{ calculateGlobalDiscountAmount() | number:'1.2-2' }}</span>
        </div>
        }
        <div class="flex justify-between text-base font-bold border-t border-gray-200 dark:border-gray-700 pt-2 mt-1">
          <span class="text-gray-900 dark:text-white">Total</span>
          <span class="text-brand-600 dark:text-brand-400">{{ currencySymbol }} {{ calculateTotal() | number:'1.2-2' }}</span>
        </div>
        @if (convertedTotal != null) {
        <!-- (V188) Total convertido justo debajo del Total, identificando la moneda de
             conversión por nombre, código y símbolo. Mismo renglón que el documento y el PDF. -->
        <div class="flex justify-between text-xs">
          <span class="text-gray-600 dark:text-gray-400">{{ convertedTotalLabel }}</span>
          <span class="font-medium text-gray-900 dark:text-white whitespace-nowrap">{{ convertedTotalSymbol }}{{ convertedTotal | number:'1.2-2' }}</span>
        </div>
        }

        <!-- (Incidente 15) Desglose de pago a crédito: anticipo, saldo financiado, intereses,
             total con financiamiento y total convertido por tipo de cambio. Solo informativo. -->
        @if (showAdvanceBreakdown || showCreditBreakdown || showFinancingBreakdown) {
        <div class="mt-2 pt-2 border-t border-dashed border-gray-200 dark:border-gray-700 space-y-1.5">
          <p class="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{{ breakdownTitle }}</p>
          @if (advancePercentValue != null) {
          <div class="flex justify-between text-xs">
            <span class="text-gray-600 dark:text-gray-400">Anticipo ({{ advancePercentValue | number:'1.0-2' }}%)</span>
            <span class="font-medium text-gray-900 dark:text-white">− {{ currencySymbol }} {{ advanceAmount | number:'1.2-2' }}</span>
          </div>
          }
          @if (showFinancingBreakdown) {
          <div class="flex justify-between text-xs">
            <span class="text-gray-600 dark:text-gray-400">Saldo financiado @if (creditTermDaysValue != null) { <span class="text-gray-400">(a {{ creditTermDaysValue }} días)</span> }</span>
            <span class="font-medium text-gray-900 dark:text-white">{{ currencySymbol }} {{ financedBalance | number:'1.2-2' }}</span>
          </div>
          <div class="flex justify-between text-xs">
            <span class="text-gray-600 dark:text-gray-400">Intereses ({{ interestRatePercentValue | number:'1.0-2' }}%)</span>
            <span class="font-medium text-gray-900 dark:text-white">{{ currencySymbol }} {{ interestAmount | number:'1.2-2' }}</span>
          </div>
          <div class="flex justify-between text-sm font-semibold pt-0.5">
            <span class="text-gray-800 dark:text-gray-200">Total con financiamiento</span>
            <span class="text-brand-600 dark:text-brand-400">{{ currencySymbol }} {{ totalWithFinancing | number:'1.2-2' }}</span>
          </div>
          } @else {
          <div class="flex justify-between text-sm font-semibold pt-0.5">
            <span class="text-gray-800 dark:text-gray-200">Saldo pendiente @if (creditTermDaysValue != null) { <span class="font-normal text-gray-400">(a {{ creditTermDaysValue }} días)</span> }</span>
            <span class="text-brand-600 dark:text-brand-400">{{ currencySymbol }} {{ financedBalance | number:'1.2-2' }}</span>
          </div>
          }
        </div>
        }
      </div>

      </div><!-- /columna derecha paso 3 -->

    </div>
  }

  <!-- ============ PASO 4 — REVISIÓN Y ENVÍO ============ -->
  @if (currentStep === 4) {
    <div class="space-y-4">

      <!-- Guardando al entrar al paso -->
      @if (savingForReview) {
      <div class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        <span class="ml-3 text-gray-500 dark:text-gray-400">Guardando cotización...</span>
      </div>
      } @else {

      <!-- Vista previa del PDF real generado por el backend (con respaldo a la vista HTML) -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 sm:p-6">
        <div class="flex items-center justify-between gap-3 mb-4">
          <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Vista previa del documento</h3>
          @if (pdfSafeUrl && !pdfLoading) {
          <button type="button" (click)="loadReviewPdf()"
            class="text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline">Actualizar</button>
          }
        </div>

        @if (pdfLoading) {
        <!-- Cargando el PDF -->
        <div class="flex justify-center items-center py-16 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/40">
          <div class="animate-spin rounded-full h-7 w-7 border-b-2 border-brand-500"></div>
          <span class="ml-3 text-sm text-gray-500 dark:text-gray-400">Generando vista previa del PDF...</span>
        </div>
        } @else if (pdfSafeUrl) {
        <!-- PDF embebido -->
        <iframe [src]="pdfSafeUrl" title="Vista previa del PDF de la cotización"
          class="w-full h-[70vh] min-h-[480px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white"></iframe>
        } @else {
        <!-- Respaldo: vista previa HTML (la generación del PDF falló) -->
        @if (pdfError) {
        <div class="mb-3 px-3.5 py-2.5 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 text-xs text-orange-700 dark:text-orange-400">
          No se pudo generar el PDF. Mostrando la vista previa del documento.
          <button type="button" (click)="loadReviewPdf()" class="ml-1 font-medium hover:underline">Reintentar</button>
        </div>
        }
        @if (reviewDoc) {
        <app-quote-document [data]="reviewDoc"></app-quote-document>
        }
        }
      </div>

      <!-- Firma del Responsable -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-3">
        <div class="flex items-center justify-between gap-3">
          <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Firma del Responsable</h3>
          @if (isResponsibleSigned) {
          <span class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
            Firmado
          </span>
          } @else {
          <span class="px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">Pendiente</span>
          }
        </div>
        @if (isResponsibleSigned) {
        <p class="text-xs text-gray-500 dark:text-gray-400">Tu firma como responsable ya aparece en el documento (vista previa arriba).</p>
        } @else {
        <p class="text-xs text-gray-500 dark:text-gray-400">Firma la cotización como responsable antes de enviarla al cliente.</p>
        <button type="button" (click)="abrirFirmaResponsable()" [disabled]="responsibleSigning || sending"
          class="w-full px-4 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
          </svg>
          Firmar como responsable
        </button>
        }
      </div>

      <!-- Firma del Cliente (informativo) -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-1.5">
        <div class="flex items-center justify-between gap-3">
          <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Firma del Cliente</h3>
          @if (isClientSigned) {
          <span class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
            Firmada
          </span>
          } @else {
          <span class="px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">Pendiente</span>
          }
        </div>
        @if (!isClientSigned) {
        <p class="text-xs text-gray-500 dark:text-gray-400">El cliente firmará la cotización desde el portal público tras recibir el enlace.</p>
        }
      </div>

      <!-- Acciones (solo en edición: la cotización ya existe tras entrar al paso) -->
      @if (isEditing) {
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-2">
        <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Acciones</h3>

        @if (isPendingApproval) {
        <!-- En espera de aprobación interna: enviar bloqueado (el backend devuelve 422) -->
        <div class="px-3.5 py-3 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800">
          <p class="text-xs text-violet-700 dark:text-violet-400">
            En espera de aprobación de <span class="font-semibold">{{ pendingApproverName }}</span>.
            Podrás firmar y enviar al cliente cuando la solicitud sea aprobada.
          </p>
        </div>
        } @else if (isApprovalBlocked) {
        <!-- Requiere aprobación interna y aún no está aprobada: ofrecer "Solicitar aprobación" -->
        <p class="text-xs text-violet-700 dark:text-violet-400 px-1">{{ APPROVAL_BLOCKED_HINT }}.</p>
        <button type="button" (click)="solicitarAprobacion()" [disabled]="saving || sending"
          class="w-full px-4 py-2.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-white bg-violet-600 hover:bg-violet-700">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          Solicitar aprobación
        </button>
        } @else {
        <!-- Firmar y enviar (acción principal) -->
        <button type="button" (click)="firmarYEnviar()" [disabled]="sending || responsibleSigning || sendingEmail"
          class="w-full px-4 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
          @if (sending) {
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          } @else {
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
          </svg>
          }
          {{ isResponsibleSigned ? 'Enviar al cliente' : 'Firmar y enviar' }}
        </button>
        }

        <!-- Descargar PDF -->
        <button type="button" (click)="descargarPdf()"
          class="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          Descargar PDF
        </button>

        <!-- Copiar enlace público (si ya se reveló en esta sesión) -->
        @if (publicLinkUrl) {
        <button type="button" (click)="copiarEnlace()"
          class="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
          </svg>
          Copiar enlace público
        </button>
        }
      </div>
      }

      }
    </div>
  }

  </div><!-- /contenido del paso (columna izquierda) -->

  <!-- ====================== SIDEBAR STICKY (columna derecha) ====================== -->
  <!-- Resumen persistente visible en todos los pasos. En móvil cae debajo del contenido. -->
  <aside class="lg:sticky lg:top-2 space-y-4 order-first lg:order-none">
    <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
      <div class="flex items-center justify-between gap-2">
        <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Resumen</h3>
        @if (isEditing && currentQuote) {
        <span class="px-2.5 py-1 text-xs font-semibold rounded-full"
          [class]="currentQuote.statusName === 'Aceptada' ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400' :
                   currentQuote.statusName === 'Publicada' ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400' :
                   currentQuote.statusName === 'Rechazada' ? 'bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400' :
                   currentQuote.statusName === 'Expirada' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                   'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'">
          {{ currentQuote.statusName }}
        </span>
        } @else {
        <span class="px-2.5 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">Borrador</span>
        }
      </div>

      <!-- Número / cliente -->
      <div class="space-y-2 text-sm">
        @if (isEditing && currentQuote?.quoteNumber) {
        <div class="flex items-center justify-between gap-2">
          <span class="text-gray-500 dark:text-gray-400">Cotización</span>
          <span class="font-medium text-gray-900 dark:text-white truncate">{{ currentQuote?.quoteNumber }}@if (currentQuote?.version) {<span class="text-gray-400"> · v{{ currentQuote?.version }}</span>}</span>
        </div>
        }
        <div class="flex items-center justify-between gap-2">
          <span class="text-gray-500 dark:text-gray-400">Negocio</span>
          <span class="font-medium text-gray-900 dark:text-white truncate text-right">
            {{ dealPickerSelected?.label || '—' }}
          </span>
        </div>
        <div class="flex items-center justify-between gap-2">
          <span class="text-gray-500 dark:text-gray-400">Cliente</span>
          <span class="font-medium text-gray-900 dark:text-white truncate text-right">
            {{ contactPickerDisplay || currentQuote?.contactFullName || '—' }}
          </span>
        </div>
        <!-- Validez (válido hasta) -->
        <div class="flex items-center justify-between gap-2">
          <span class="text-gray-500 dark:text-gray-400">Válido hasta</span>
          <span class="font-medium text-right" [class]="dateRangeError ? 'text-error-600 dark:text-error-400' : 'text-gray-900 dark:text-white'">
            {{ form.get('datetimeExpires')?.value || '—' }}
          </span>
        </div>
        <!-- Ítems -->
        <div class="flex items-center justify-between gap-2">
          <span class="text-gray-500 dark:text-gray-400">Ítems</span>
          <span class="font-medium text-gray-900 dark:text-white">{{ items.length }}</span>
        </div>
      </div>

      <!-- Totales en vivo: secundarios (subtotal/descuento/impuesto) + Total destacado -->
      <div class="border-t border-gray-100 dark:border-gray-800 pt-3 space-y-1.5 text-sm">
        <div class="flex items-center justify-between">
          <span class="text-gray-500 dark:text-gray-400">Subtotal</span>
          <span class="font-medium text-gray-900 dark:text-white">{{ currencySymbol }} {{ calculateSubtotal() | number:'1.2-2' }}</span>
        </div>
        @if (calculateGlobalDiscountAmount() > 0) {
        <div class="flex items-center justify-between">
          <span class="text-gray-500 dark:text-gray-400">Descuento</span>
          <span class="font-medium text-error-600 dark:text-error-400">- {{ currencySymbol }} {{ calculateGlobalDiscountAmount() | number:'1.2-2' }}</span>
        </div>
        }
        <div class="flex items-center justify-between">
          <span class="text-gray-500 dark:text-gray-400">{{ effectiveTaxLabel }} ({{ (effectiveTaxRate * 100) | number:'1.0-2' }}%)</span>
          <span class="font-medium text-gray-900 dark:text-white">{{ currencySymbol }} {{ calculateTax() | number:'1.2-2' }}</span>
        </div>
      </div>

      <!-- Total destacado -->
      <div class="rounded-xl bg-brand-50 dark:bg-brand-900/20 border border-brand-100 dark:border-brand-800/40 px-4 py-3">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-brand-600/80 dark:text-brand-400/80 leading-none">Total</p>
        <p class="mt-1 text-2xl font-bold text-brand-700 dark:text-brand-400 leading-tight break-words">{{ currencySymbol }} {{ calculateTotal() | number:'1.2-2' }}</p>
      </div>

      <!-- Accesos rápidos contextuales -->
      @if (isEditing) {
      <div class="border-t border-gray-100 dark:border-gray-800 pt-3 space-y-1">
        <button type="button" (click)="verVersiones()"
          class="w-full flex items-center gap-2 px-2 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors">
          <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          Historial de versiones
        </button>
      </div>
      }
    </div>
  </aside>

  </div><!-- /layout 2 columnas -->

  <!-- ====================== FOOTER DEL MODAL: navegación ====================== -->
  </div><!-- /padding del cuerpo con scroll -->
  </div><!-- /cuerpo con scroll interno -->

  <!-- Footer fijo dentro del panel del modal -->
  <div class="shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
    <div class="max-w-4xl lg:max-w-5xl xl:max-w-[1500px] 2xl:max-w-[1760px] 3xl:max-w-[1960px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
      <!-- Total compacto (refuerzo del resumen, visible también en móvil) -->
      <div class="flex flex-col">
        <span class="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 leading-none">Total</span>
        <span class="text-lg font-bold text-brand-600 dark:text-brand-400 leading-tight">{{ currencySymbol }} {{ calculateTotal() | number:'1.2-2' }}</span>
      </div>

      <div class="flex-1"></div>

      <!-- Cancelar -->
      <button type="button" (click)="cancelar()"
        class="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
        Cancelar
      </button>

      <!-- Atrás -->
      @if (currentStep > 1) {
      <button type="button" (click)="prevStep()"
        class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
        Atrás
      </button>
      }

      <!-- Siguiente / Guardar -->
      @if (currentStep < totalSteps) {
      <div class="flex flex-col items-end gap-1">
        <button type="button" (click)="nextStep()"
          [title]="currentStepBlockReason || 'Continuar al siguiente paso'"
          [class.opacity-60]="currentStepBlockReason"
          class="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors">
          Siguiente
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
        @if (currentStepBlockReason) {
        <span class="hidden sm:block text-[11px] text-gray-400 dark:text-gray-500 max-w-[16rem] text-right leading-tight">{{ currentStepBlockReason }}</span>
        }
      </div>
      } @else {
      <button type="submit" [disabled]="saving"
        class="inline-flex items-center justify-center gap-2 px-5 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
        @if (saving) {
        <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
        }
        {{ isEditing ? 'Actualizar' : 'Guardar' }} cotización
      </button>
      }
    </div>
  </div>
</form>
}
  </div><!-- /panel del modal -->
</div><!-- /modal fullscreen -->

<!-- Modal: historial de versiones -->
@if (showVersionsModal) {
<div class="fixed inset-0 z-modal-top flex items-center justify-center">
  <div class="absolute inset-0 bg-black/40" (click)="cerrarVersiones()"></div>
  <div class="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl mx-4 sm:mx-6 max-h-[80vh] flex flex-col">
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
      <div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Historial de versiones</h3>
        @if (currentQuote) {
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {{ currentQuote.quoteNumber }} — todas las versiones comparten el mismo número.
        </p>
        }
      </div>
      <button type="button" (click)="cerrarVersiones()"
        class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <!-- Body -->
    <div class="flex-1 overflow-y-auto px-6 py-4">
      @if (loadingVersions) {
      <div class="flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
        <span class="ml-3 text-sm text-gray-500 dark:text-gray-400">Cargando versiones...</span>
      </div>
      } @else if (versions.length === 0) {
      <p class="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No hay versiones registradas.</p>
      } @else {
      <div class="space-y-2">
        @for (v of versions; track v.id) {
        <div class="flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors"
          [class]="v.isCurrent
            ? 'border-brand-300 bg-brand-50/50 dark:border-brand-700 dark:bg-brand-900/10'
            : 'border-gray-200 dark:border-gray-700'">
          <!-- Versión -->
          <span class="shrink-0 w-10 text-center px-2 py-1 text-xs font-bold rounded"
            [class]="v.isCurrent
              ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-400'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'">
            v{{ v.version }}
          </span>

          <!-- Estado + fecha + notas -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-sm font-medium text-gray-900 dark:text-white">{{ v.statusName }}</span>
              @if (v.isCurrent) {
              <span class="px-2 py-0.5 text-xs font-medium rounded-full bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
                Vigente
              </span>
              }
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Creada: {{ formatDate(v.datetimeCreated) }}
            </p>
            @if (v.versionNotes) {
            <p class="text-xs text-gray-500 dark:text-gray-400 italic truncate" [title]="v.versionNotes">{{ v.versionNotes }}</p>
            }
          </div>

          <!-- Restaurar (solo versiones históricas) -->
          @if (!v.isCurrent) {
          <button type="button" (click)="restaurarVersion(v)" [disabled]="restoringVersionId !== null"
            class="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50">
            @if (restoringVersionId === v.id) {
            <div class="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-current"></div>
            } @else {
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
            </svg>
            }
            Restaurar
          </button>
          }
        </div>
        }
      </div>
      }
    </div>

    <!-- Footer -->
    <div class="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
      <button type="button" (click)="cerrarVersiones()"
        class="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
        Cerrar
      </button>
    </div>
  </div>
</div>
}

<!-- Modal: enviar cotización por correo al cliente (#28) -->
@if (showSendEmailModal) {
<div class="fixed inset-0 z-modal-top flex items-center justify-center">
  <div class="absolute inset-0 bg-black/40" (click)="cerrarEnviarCorreo()"></div>
  <div class="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg mx-4 sm:mx-6 max-h-[85vh] flex flex-col">
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
      <div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Enviar cotización por correo</h3>
        @if (currentQuote) {
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {{ currentQuote.quoteNumber }} — v{{ currentQuote.version }}
        </p>
        }
      </div>
      <button type="button" (click)="cerrarEnviarCorreo()" [disabled]="sendingEmail"
        class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <!-- Body -->
    <div class="flex-1 overflow-y-auto px-6 py-4 space-y-4">
      <!-- Aviso: el enlace anterior deja de funcionar -->
      <div class="flex items-start gap-2.5 px-3.5 py-3 rounded-lg bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800">
        <svg class="w-4 h-4 mt-0.5 shrink-0 text-warning-600 dark:text-warning-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
        </svg>
        <p class="text-xs text-warning-700 dark:text-warning-400">
          Se generará un enlace nuevo para el cliente; el enlace compartido anteriormente dejará de funcionar.
        </p>
      </div>

      <!-- Error del envío (422 sin destinatario, 409 sin canal, 400 validación, etc.) -->
      @if (sendEmailError) {
      <div class="px-3.5 py-3 rounded-lg bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800">
        <p class="text-xs text-error-700 dark:text-error-400">{{ sendEmailError }}</p>
      </div>
      }

      <!-- Destinatarios: solo el contacto principal precargado como chip; el resto como sugerencias clicables -->
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Para</label>

        @if (loadingDealContacts) {
        <div class="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
          <div class="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-brand-500"></div>
          <span class="text-xs text-gray-500 dark:text-gray-400">Cargando contactos del negocio...</span>
        </div>
        } @else {

        <!-- Chips de destinatarios -->
        @if (sendRecipients.length > 0) {
        <div class="flex flex-wrap gap-2 mb-2">
          @for (chip of sendRecipients; track chip.email) {
          <span [title]="chipTitle(chip)"
            class="inline-flex items-center gap-1.5 max-w-full pl-3 pr-1.5 py-1 rounded-full text-xs border bg-brand-50 border-brand-200 text-brand-700 dark:bg-brand-900/20 dark:border-brand-800 dark:text-brand-400">
            <span class="truncate font-medium">{{ chip.name || chip.email }}</span>
            @if (chip.name) {
            <span class="truncate text-brand-500/80 dark:text-brand-400/70">{{ chip.email }}</span>
            }
            @if (chip.isPrimary) {
            <span class="shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
              Principal
            </span>
            }
            <button type="button" (click)="quitarDestinatario($index)" [disabled]="sendingEmail"
              [attr.aria-label]="'Quitar ' + chip.email"
              class="shrink-0 p-0.5 rounded-full text-brand-400 hover:text-brand-700 hover:bg-brand-100 dark:hover:text-brand-200 dark:hover:bg-brand-900/40 transition-colors disabled:opacity-50">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </span>
          }
        </div>
        } @else {
        <p class="mb-2 text-xs text-warning-600 dark:text-warning-400">
          Agrega al menos un destinatario para poder enviar.
        </p>
        }

        <!-- Agregar destinatario por correo -->
        <div class="flex gap-2">
          <input type="text" [(ngModel)]="sendRecipientInput" [disabled]="sendingEmail"
            (keydown.enter)="$event.preventDefault(); agregarDestinatario()"
            placeholder="correo@empresa.com (Enter para agregar; varios separados por coma)"
            class="flex-1 px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
            [class]="sendRecipientInputError ? 'border-error-300 dark:border-error-700' : 'border-gray-300 dark:border-gray-600'"/>
          <button type="button" (click)="agregarDestinatario()" [disabled]="sendingEmail"
            title="Agregar destinatario"
            class="shrink-0 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
          </button>
        </div>
        @if (sendRecipientInputError) {
        <p class="mt-1 text-xs text-error-600 dark:text-error-400">{{ sendRecipientInputError }}</p>
        }

        <!-- Sugerencias: demás contactos del negocio con correo (clic para agregar como destinatario) -->
        @if (sendSuggestions.length > 0) {
        <div class="mt-2.5">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Sugerencias del negocio:</p>
          <div [class]="showAllSuggestions
              ? 'flex flex-wrap items-center gap-1.5 max-h-40 overflow-y-auto pr-1'
              : 'flex flex-wrap items-center gap-1.5'">
            @for (s of visibleSuggestions; track s.email) {
            <button type="button" (click)="seleccionarSugerencia(s)" [disabled]="sendingEmail"
              [title]="chipTitle(s)" [attr.aria-label]="'Agregar a ' + s.email + ' como destinatario'"
              class="inline-flex items-center gap-1 max-w-full px-2.5 py-1 rounded-full text-xs border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/60 text-gray-600 dark:text-gray-300 hover:border-brand-400 dark:hover:border-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/20 hover:text-brand-700 dark:hover:text-brand-400 transition-colors disabled:opacity-50 cursor-pointer">
              <span class="shrink-0 font-semibold">+</span>
              <span class="truncate">{{ s.name || s.email }}</span>
              @if (s.roleName) {
              <span class="shrink-0 text-gray-400 dark:text-gray-500">· {{ s.roleName }}</span>
              }
            </button>
            }
            @if (!showAllSuggestions && hiddenSuggestionsCount > 0) {
            <button type="button" (click)="toggleVerMasSugerencias()" [disabled]="sendingEmail"
              class="shrink-0 px-1.5 py-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline disabled:opacity-50">
              ver {{ hiddenSuggestionsCount }} más &#9662;
            </button>
            } @else if (showAllSuggestions && sendSuggestions.length > SUGGESTIONS_VISIBLE) {
            <button type="button" (click)="toggleVerMasSugerencias()" [disabled]="sendingEmail"
              class="shrink-0 px-1.5 py-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline disabled:opacity-50">
              ver menos &#9652;
            </button>
            }
          </div>
        </div>
        }

        <!-- Contactos del negocio sin correo (informativo) -->
        @if (dealContactsSinCorreo.length > 0) {
        <p class="mt-2 text-xs text-gray-400 dark:text-gray-500">
          Sin correo: {{ dealContactsSinCorreo.join(', ') }}
        </p>
        }

        }
      </div>

      <!-- CC -->
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CC (opcional)</label>
        <input type="text" [(ngModel)]="sendCc" [disabled]="sendingEmail"
          placeholder="correo1@empresa.com, correo2@empresa.com"
          class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"/>
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Varios correos separados por coma.</p>
      </div>

      <!-- Mensaje personal -->
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mensaje personal (opcional)</label>
        <textarea [(ngModel)]="sendPersonalMessage" [disabled]="sendingEmail" rows="4"
          [attr.maxlength]="SEND_EMAIL_MESSAGE_MAX"
          placeholder="Mensaje que se incluirá en el correo junto a los botones de ver y descargar la cotización"
          class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 resize-none"></textarea>
        <p class="mt-1 text-xs text-right"
          [class]="sendPersonalMessage.length >= SEND_EMAIL_MESSAGE_MAX ? 'text-error-600 dark:text-error-400' : 'text-gray-400 dark:text-gray-500'">
          {{ sendPersonalMessage.length }}/{{ SEND_EMAIL_MESSAGE_MAX }}
        </p>
        @if (!sendPersonalMessage && currentQuote?.openingMessage) {
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Si lo dejas vacío, el correo usará el mensaje de apertura de la cotización.
        </p>
        }
      </div>
    </div>

    <!-- Footer -->
    <div class="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
      <button type="button" (click)="cerrarEnviarCorreo()" [disabled]="sendingEmail"
        class="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors disabled:opacity-50">
        Cancelar
      </button>
      <button type="button" (click)="enviarCorreo()"
        [disabled]="sendingEmail || (sendRecipients.length === 0 && !sendRecipientInput.trim())"
        [title]="sendRecipients.length === 0 && !sendRecipientInput.trim() ? 'Agrega al menos un destinatario' : ''"
        class="px-4 py-2 text-sm font-medium text-white bg-blue-light-500 hover:bg-blue-light-600 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2">
        @if (sendingEmail) {
        <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
        Enviando...
        } @else {
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
        </svg>
        Enviar
        }
      </button>
    </div>
  </div>
</div>
}

<!-- Modal: firma del responsable (V74) -->
@if (showResponsibleSignModal) {
<div class="fixed inset-0 z-modal-top flex items-center justify-center p-4">
  <div class="absolute inset-0 bg-black/50" (click)="cerrarFirmaResponsable()"></div>
  <div class="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col">

    <!-- Header -->
    <div class="flex items-start justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
      <div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Firmar como responsable</h3>
        @if (currentQuote) {
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ currentQuote.quoteNumber }} · v{{ currentQuote.version }}</p>
        }
      </div>
      <button type="button" (click)="cerrarFirmaResponsable()" [disabled]="responsibleSigning"
        class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <!-- Body -->
    <div class="flex-1 overflow-y-auto px-6 py-4 space-y-4">

      <!-- Tabs de método de firma -->
      <div>
        <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Método de firma</p>
        <div class="grid grid-cols-4 gap-1 p-1 rounded-lg bg-gray-100 dark:bg-gray-800">
          <button type="button" (click)="setSignTab('registered')"
            class="px-2 py-1.5 rounded-md text-sm font-medium transition-colors"
            [class]="signTab === 'registered' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'">
            Registrada
          </button>
          <button type="button" (click)="setSignTab('drawn')"
            class="px-2 py-1.5 rounded-md text-sm font-medium transition-colors"
            [class]="signTab === 'drawn' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'">
            Dibujar
          </button>
          <button type="button" (click)="setSignTab('typed')"
            class="px-2 py-1.5 rounded-md text-sm font-medium transition-colors"
            [class]="signTab === 'typed' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'">
            Escribir
          </button>
          <button type="button" (click)="setSignTab('upload')"
            class="px-2 py-1.5 rounded-md text-sm font-medium transition-colors"
            [class]="signTab === 'upload' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'">
            Subir
          </button>
        </div>
      </div>

      <!-- Tab: Firma registrada -->
      @if (signTab === 'registered') {
      <div class="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 px-4 py-6 text-center bg-gray-50 dark:bg-gray-800/40">
        <svg class="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <p class="text-sm text-gray-600 dark:text-gray-300">Se usará tu firma registrada como aprobador.</p>
        <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Si no tienes una firma registrada, elige otra pestaña para dibujarla, escribirla o subirla.</p>
      </div>
      }

      <!-- Tab: Dibujar -->
      @if (signTab === 'drawn') {
      <div>
        <div class="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-100 overflow-hidden">
          <canvas #respSignCanvas class="block w-full h-44 touch-none cursor-crosshair"
            (pointerdown)="onSignPointerDown($event)"
            (pointermove)="onSignPointerMove($event)"
            (pointerup)="onSignPointerUp($event)"
            (pointercancel)="onSignPointerUp($event)"></canvas>
        </div>
        <div class="flex items-center justify-between mt-1.5">
          <p class="text-xs text-gray-400 dark:text-gray-500">Dibuja tu firma dentro del recuadro.</p>
          <button type="button" (click)="limpiarFirmaResponsable()" [disabled]="!hasDrawnSignature"
            class="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-error-600 dark:hover:text-error-400 disabled:opacity-40 transition-colors">
            Limpiar
          </button>
        </div>
      </div>
      }

      <!-- Tab: Escribir -->
      @if (signTab === 'typed') {
      <div>
        <div class="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 h-32 flex items-center justify-center px-4 bg-white dark:bg-gray-800 mb-2">
          <span class="text-3xl text-gray-800 dark:text-white italic text-center break-words max-w-full"
            style="font-family: 'Brush Script MT', 'Segoe Script', 'Lucida Handwriting', cursive;">
            {{ typedSignaturePreview }}
          </span>
        </div>
        <input type="text" [(ngModel)]="typedSignatureName" [ngModelOptions]="{standalone: true}"
          placeholder="Tu nombre completo"
          class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"/>
      </div>
      }

      <!-- Tab: Subir -->
      @if (signTab === 'upload') {
      <div>
        @if (uploadedSignature) {
        <div class="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 h-44 flex items-center justify-center bg-white dark:bg-gray-100 p-3">
          <img [src]="uploadedSignature" alt="Firma subida" class="max-h-full max-w-full object-contain">
        </div>
        <div class="flex justify-end mt-1.5">
          <button type="button" (click)="quitarImagenFirmaResponsable()"
            class="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-error-600 dark:hover:text-error-400 transition-colors">
            Quitar imagen
          </button>
        </div>
        } @else {
        <label class="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 h-44 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-colors bg-white dark:bg-gray-800">
          <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
          <span class="text-sm text-gray-600 dark:text-gray-300 font-medium">Selecciona una imagen de tu firma</span>
          <span class="text-xs text-gray-400">PNG o JPG, máximo 1 MB</span>
          <input type="file" accept="image/png,image/jpeg" class="hidden" (change)="onResponsibleSignatureFileSelected($event)">
        </label>
        }
        @if (uploadSignatureError) {
        <p class="text-xs text-error-600 dark:text-error-400 mt-1.5">{{ uploadSignatureError }}</p>
        }
      </div>
      }

      @if (responsibleSignError) {
      <div class="rounded-lg bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 px-3 py-2">
        <p class="text-sm text-error-600 dark:text-error-400">{{ responsibleSignError }}</p>
      </div>
      }
    </div>

    <!-- Footer -->
    <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
      <button type="button" (click)="cerrarFirmaResponsable()" [disabled]="responsibleSigning"
        class="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors disabled:opacity-50">
        Cancelar
      </button>
      <button type="button" (click)="confirmarFirmaResponsable()" [disabled]="responsibleSigning"
        class="px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-brand-600 hover:bg-brand-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
        @if (responsibleSigning) {
        <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
        Firmando...
        } @else {
        Firmar
        }
      </button>
    </div>
  </div>
</div>
}

<!-- Modal: solicitar aprobación interna (RF-001) -->
<app-quote-request-approval-modal
  [isOpen]="showRequestApprovalModal"
  [quoteId]="quoteId"
  [quoteNumber]="currentQuote?.quoteNumber || ''"
  (closed)="cerrarSolicitarAprobacion()"
  (requested)="onAprobacionSolicitada()">
</app-quote-request-approval-modal>

<!-- Modal: importar ítems desde una plantilla de contenido (V76) -->
@if (showImportItemsModal) {
<div class="fixed inset-0 z-modal-top flex items-center justify-center p-4">
  <div class="absolute inset-0 bg-black/50" (click)="closeImportItemsModal()"></div>
  <div class="relative w-full max-w-lg max-h-[80vh] flex flex-col rounded-2xl bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700">
    <!-- Header -->
    <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
      <div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white">Importar ítems</h3>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Los ítems de la plantilla se agregan al final de la lista.</p>
      </div>
      <button type="button" (click)="closeImportItemsModal()"
        class="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <!-- Body -->
    <div class="flex-1 overflow-y-auto px-5 py-4">
      @if (loadingContentTemplates) {
      <div class="flex justify-center items-center py-10">
        <div class="animate-spin rounded-full h-7 w-7 border-b-2 border-brand-500"></div>
      </div>
      } @else if (contentTemplates.length === 0) {
      <div class="text-center py-10">
        <p class="text-sm text-gray-500 dark:text-gray-400">No hay plantillas de contenido disponibles.</p>
      </div>
      } @else {
      <div class="space-y-2">
        @for (tpl of contentTemplates; track tpl.id) {
        <button type="button" (click)="importFromContentTemplate(tpl)"
          [disabled]="importingTemplateId !== null"
          class="w-full text-left px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-brand-400 dark:hover:border-brand-500 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          <div class="flex items-center justify-between gap-3">
            <div class="min-w-0">
              <span class="block text-sm font-medium text-gray-900 dark:text-white truncate">{{ tpl.name }}</span>
              @if (tpl.description) {
              <span class="block text-xs text-gray-500 dark:text-gray-400 truncate">{{ tpl.description }}</span>
              }
            </div>
            @if (importingTemplateId === tpl.id) {
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500 shrink-0"></div>
            } @else {
            <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            }
          </div>
        </button>
        }
      </div>
      }
    </div>
  </div>
</div>
}
